import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  ColDef,
  GridReadyEvent,
  RowClickedEvent,
} from "ag-grid-community";
import { AgGridTable, ConfirmDialog, LoadingState } from "components";
import { useReturnOrder, useReturnOrderStore } from "hooks";
import type {
  ReturnOrderItem,
  ReturnOrderForm,
  ReturnOrderQuery,
  ReturnOrderRow,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import type { QueryState } from "components/ui-common/AgGridTable";
import { Plus } from "lucide-react";

const todayString = () => new Date().toISOString().slice(0, 10);

const ReturnOrders: React.FC = () => {
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const addRowRef = useRef<ReturnOrderRow | null>(null);
  const [addRowVersion, setAddRowVersion] = useState(0);
  const [queryParams, setQueryParams] = useState<ReturnOrderQuery>({
    page: 1,
    pageSize: 20,
  });
  const [selectedReturnOrderId, setSelectedReturnOrderId] = useState<
    number | string | "pinned" | null
  >(null);
  const [itemDrafts, setItemDrafts] = useState<ReturnOrderItem[]>([]);
  const [showInlineAddRow, setShowInlineAddRow] = useState<any>("");
  const [confirmRow, setConfirmRow] = useState<ReturnOrderRow | null>(null);

  const {
    rows,
    setRows,
    upsertRow,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useReturnOrderStore();

  const selectedReturnOrder = useMemo(() => {
    if (selectedReturnOrderId === "pinned") {
      return addRowRef.current ?? null;
    }
    if (!rows.length || selectedReturnOrderId === null) return null;
    return (
      rows.find((row) => String(row.id) === String(selectedReturnOrderId)) ??
      null
    );
  }, [rows, selectedReturnOrderId]);

  const {
    fetchList,
    createReturnOrder,
    updateReturnOrder,
    deleteReturnOrder,
  } = useReturnOrder();

  const {
    data: returnOrderData,
    isFetching,
    refetch,
  } = fetchList(queryParams, true);

  const parseNumber = useCallback((value: unknown) => {
    if (value === null || value === undefined || value === "") return 0;
    if (typeof value === "number" && Number.isFinite(value)) return value;
    const parsed = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const parseBoolean = useCallback((value: unknown) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const lowered = value.toLowerCase();
      if (["true", "1", "yes", "on"].includes(lowered)) return true;
      if (["false", "0", "no", "off"].includes(lowered)) return false;
    }
    return Boolean(value);
  }, []);

  const normalizeAttachments = useCallback((value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === "string" ? item : String(item ?? "")))
        .map((item) => item.trim())
        .filter(Boolean);
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => (typeof item === "string" ? item : String(item ?? "")))
            .map((item) => item.trim())
            .filter(Boolean);
        }
      } catch (error) {
        // ignore invalid JSON strings
      }

      return trimmed
        .split(/[,\\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }, []);

  const sanitizeItems = useCallback(
    (items?: ReturnOrderItem[]) =>
      Array.isArray(items)
        ? items
          .map((item) => {
            const quantity = parseNumber(item.quantity);
            const saleUnitPrice = parseNumber(item.sale_unit_price);
            const returnUnitPrice = parseNumber(item.return_unit_price);
            const convertQuantity = parseNumber(item.convert_quantity);
            const convertUnitPrice = parseNumber(item.convert_unit_price);
            const saleAmount =
              item.sale_amount !== undefined && item.sale_amount !== null
                ? parseNumber(item.sale_amount)
                : quantity * saleUnitPrice || 0;
            const returnAmount =
              item.return_amount !== undefined && item.return_amount !== null
                ? parseNumber(item.return_amount)
                : quantity * returnUnitPrice || 0;
            const convertReturnPrice =
              item.convert_return_price !== undefined &&
                item.convert_return_price !== null
                ? parseNumber(item.convert_return_price)
                : convertQuantity * convertUnitPrice || 0;

            return {
              wms_id: item.wms_id ?? undefined,
              category_id: item.category_id ?? undefined,
              product_id: item.product_id ?? undefined,
              product_name: item.product_name
                ? String(item.product_name).trim()
                : undefined,
              unit_id: item.unit_id ?? undefined,
              quantity,
              sale_unit_price: saleUnitPrice,
              sale_amount: saleAmount,
              return_unit_price: returnUnitPrice,
              return_amount: returnAmount,
              convert_unit_id: item.convert_unit_id ?? undefined,
              convert_operator: item.convert_operator
                ? String(item.convert_operator).trim()
                : undefined,
              convert_ratio:
                item.convert_ratio !== undefined &&
                  item.convert_ratio !== null
                  ? parseNumber(item.convert_ratio)
                  : undefined,
              convert_quantity: convertQuantity,
              convert_unit_price: convertUnitPrice,
              convert_return_price: convertReturnPrice,
            };
          })
          .filter((item) => {
            const hasQuantity = Boolean(item.quantity);
            const hasProduct =
              item.product_id !== undefined || Boolean(item.product_name);
            const hasAmount =
              Boolean(item.return_amount) ||
              Boolean(item.sale_amount) ||
              Boolean(item.convert_return_price);
            return hasQuantity || hasProduct || hasAmount;
          })
        : [],
    [parseNumber],
  );

  const withItemFields = useCallback(
    (order: ReturnOrderRow): ReturnOrderRow => {
      const normalizedItems = sanitizeItems(order.items);
      const totalReturnAmount = normalizedItems.reduce(
        (sum, item) =>
          sum +
          (parseNumber(
            item.return_amount ??
            item.convert_return_price ??
            item.sale_amount ??
            0,
          ) || 0),
        0,
      );

      return {
        ...order,
        attachments: normalizeAttachments(order.attachments),
        refund_now: parseBoolean(order.refund_now),
        no_refund: parseBoolean(order.no_refund),
        items: normalizedItems,
        total_return_amount: totalReturnAmount || undefined,
      };
    },
    [normalizeAttachments, parseBoolean, parseNumber, sanitizeItems],
  );

  useEffect(() => {
    if (returnOrderData?.data) {
      const mapped =
        (returnOrderData.data as ReturnOrderRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
    }
  }, [returnOrderData, setRows, withItemFields]);

  useEffect(() => {
    if (selectedReturnOrderId === "pinned") return;

    if (!rows.length) {
      setSelectedReturnOrderId(null);
      return;
    }

    if (selectedReturnOrderId === null) return;

    const exists = rows.some(
      (row) => String(row.id) === String(selectedReturnOrderId),
    );

    if (!exists) {
      setSelectedReturnOrderId(null);
    }
  }, [rows, selectedReturnOrderId]);

  useEffect(() => {
    setItemDrafts(
      selectedReturnOrder ? sanitizeItems(selectedReturnOrder.items) : [],
    );
  }, [sanitizeItems, selectedReturnOrder]);

  useEffect(() => {
    setLoading(
      Boolean(
        isFetching ||
        createReturnOrder.isPending ||
        updateReturnOrder.isPending ||
        deleteReturnOrder.isPending,
      ),
    );
  }, [
    isFetching,
    createReturnOrder.isPending,
    updateReturnOrder.isPending,
    deleteReturnOrder.isPending,
    setLoading,
  ]);

  const columns = useMemo<ColDef<ReturnOrderRow>[]>(() => {
    return [
      {
        headerName: "Ngày nhập lại",
        field: "return_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Hạn thanh toán",
        field: "due_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
      },
      {
        headerName: "Mã đơn bán",
        field: "sale_order_code",
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Khách hàng",
        field: "customer_name",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Người nhận",
        field: "receiver_name",
        minWidth: 180,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "PT thanh toán",
        field: "payment_method",
        minWidth: 160,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Hoàn tiền ngay",
        field: "refund_now",
        minWidth: 140,
        filter: "agTextColumnFilter",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["true", "false"] },
        valueFormatter: (params) => (params.value ? "Có" : "Không"),
        valueSetter: (params) => {
          (params.data as ReturnOrderRow).refund_now = parseBoolean(
            params.newValue,
          );
          return true;
        },
      },
      {
        headerName: "Không hoàn tiền",
        field: "no_refund",
        minWidth: 150,
        filter: "agTextColumnFilter",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["true", "false"] },
        valueFormatter: (params) => (params.value ? "Có" : "Không"),
        valueSetter: (params) => {
          (params.data as ReturnOrderRow).no_refund = parseBoolean(
            params.newValue,
          );
          return true;
        },
      },
      {
        headerName: "Mô tả",
        field: "description",
        minWidth: 200,
        flex: 1,
        filter: "agTextColumnFilter",
      },
      {
        headerName: "Tệp đính kèm",
        field: "attachments",
        minWidth: 180,
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          if (Array.isArray(params.value)) {
            return params.value.join(", ");
          }
          return params.value || "";
        },
      },
      {
        headerName: "Tổng tiền trả",
        field: "total_return_amount",
        minWidth: 160,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          parseNumber(params.value ?? 0).toLocaleString("vi-VN"),
      },
    ];
  }, [parseBoolean, parseNumber]);

  const normalizePayload = useCallback(
    (row: Partial<ReturnOrderRow>): ReturnOrderForm => {
      const attachments = normalizeAttachments(row.attachments);
      const sanitizedItems = sanitizeItems(row.items);

      const customerId =
        row.customer_id !== undefined &&
          row.customer_id !== null &&
          row.customer_id !== ""
          ? Number(row.customer_id)
          : undefined;
      const receiverId =
        row.receiver_id !== undefined &&
          row.receiver_id !== null &&
          row.receiver_id !== ""
          ? Number(row.receiver_id)
          : undefined;
      const saleOrderId =
        row.sale_order_id !== undefined &&
          row.sale_order_id !== null &&
          row.sale_order_id !== ""
          ? Number(row.sale_order_id)
          : undefined;

      return {
        return_date: String(row.return_date ?? "").trim(),
        due_date: row.due_date
          ? String(row.due_date).trim()
          : String(row.return_date ?? "").trim(),
        customer_id: customerId,
        customer_name: row.customer_name ? String(row.customer_name).trim() : "",
        receiver_id: receiverId,
        receiver_name: row.receiver_name ? String(row.receiver_name).trim() : "",
        sale_order_id: saleOrderId,
        sale_order_code: row.sale_order_code
          ? String(row.sale_order_code).trim()
          : "",
        payment_method: row.payment_method
          ? String(row.payment_method).trim()
          : "",
        refund_now: parseBoolean(row.refund_now),
        no_refund: parseBoolean(row.no_refund),
        description: row.description ? String(row.description).trim() : "",
        attachments,
        items: sanitizedItems,
      };
    },
    [normalizeAttachments, parseBoolean, sanitizeItems],
  );

  type PersistMeta = {
    pinned?: boolean;
    onRevert?: () => void;
    onSuccess?: () => void;
    refetch?: boolean;
  };

  const resetInlineAddRow = useCallback(() => {
    addRowRef.current = {
      return_date: todayString(),
      due_date: todayString(),
      customer_id: undefined,
      customer_name: "",
      receiver_id: undefined,
      receiver_name: "",
      sale_order_id: undefined,
      sale_order_code: "",
      payment_method: "",
      refund_now: true,
      no_refund: false,
      description: "",
      attachments: [],
      items: [],
    } as ReturnOrderRow;
    setAddRowVersion((v) => v + 1);
  }, []);

  const persistRow = useCallback(
    (row: Partial<ReturnOrderRow>, meta?: PersistMeta) => {
      const composedRow =
        meta?.pinned && addRowRef.current
          ? { ...addRowRef.current, ...row, items: addRowRef.current.items }
          : row;
      const payload = normalizePayload(composedRow);

      if (!payload.return_date || !payload.sale_order_code) {
        SnackbarUtils.error("Ngày nhập lại và Mã đơn bán là bắt buộc");
        meta?.onRevert?.();
        return;
      }

      if (!payload.customer_name || !payload.receiver_name) {
        SnackbarUtils.error("Vui lòng nhập Khách hàng và Người nhận");
        meta?.onRevert?.();
        return;
      }

      if (!payload.items?.length) {
        SnackbarUtils.error("Vui lòng thêm ít nhất 1 dòng hàng hoàn trả");
        meta?.onRevert?.();
        return;
      }

      const shouldCreate = meta?.pinned || !row.id;
      if (shouldCreate) {
        createReturnOrder.mutate(payload, {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(withItemFields(res.data as ReturnOrderRow));
            }
            resetInlineAddRow();
            if (meta?.refetch !== false) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Thêm chứng từ nhập lại không thành công");
            meta?.onRevert?.();
          },
        });
        return;
      }

      if (!row.id) {
        SnackbarUtils.error("Không xác định được chứng từ cần cập nhật");
        meta?.onRevert?.();
        return;
      }

      updateReturnOrder.mutate(
        { id: row.id, ...payload },
        {
          onSuccess: (res) => {
            if (res?.data) {
              upsertRow(withItemFields(res.data as ReturnOrderRow));
            } else {
              upsertRow(
                withItemFields({ ...(row as ReturnOrderRow), ...payload }),
              );
            }
            if (meta?.refetch) {
              refetch();
            }
            meta?.onSuccess?.();
          },
          onError: () => {
            SnackbarUtils.error("Cập nhật chứng từ nhập lại không thành công");
            meta?.onRevert?.();
          },
        },
      );
    },
    [
      createReturnOrder,
      normalizePayload,
      refetch,
      resetInlineAddRow,
      updateReturnOrder,
      upsertRow,
      withItemFields,
    ],
  );

  const handleSaveRow = useCallback(
    (row: ReturnOrderRow, meta?: { pinned?: boolean }) => {
      persistRow(row, {
        pinned: meta?.pinned,
        refetch: meta?.pinned || !row.id,
        onSuccess: () => {
          setShowInlineAddRow("");
          setEditingId(null);
        },
      });
    },
    [persistRow, setEditingId],
  );

  const handleEditRow = useCallback(
    (params) => {
      setShowInlineAddRow("edit");
      setEditingId(params.data.id);
    },
    [setEditingId],
  );

  const handleRowClick = useCallback((event: RowClickedEvent<ReturnOrderRow>) => {
    if (event.node?.rowPinned) {
      setSelectedReturnOrderId("pinned");
      return;
    }
    setSelectedReturnOrderId(event.data?.id ?? null);
  }, []);

  const handleDeleteRow = useCallback((row: ReturnOrderRow) => {
    setConfirmRow(row);
    setShowInlineAddRow("");
  }, []);

  const handleAddItem = useCallback(() => {
    if (!selectedReturnOrder && selectedReturnOrderId !== "pinned") return;
    setItemDrafts((prev) => [
      ...prev,
      {
        product_name: "",
        quantity: 1,
        sale_unit_price: 0,
        return_unit_price: 0,
        sale_amount: 0,
        return_amount: 0,
        convert_operator: "*",
      },
    ]);
  }, [selectedReturnOrder, selectedReturnOrderId]);

  const numericItemKeys = useMemo<(keyof ReturnOrderItem)[]>(
    () => [
      "quantity",
      "sale_unit_price",
      "sale_amount",
      "return_unit_price",
      "return_amount",
      "convert_ratio",
      "convert_quantity",
      "convert_unit_price",
      "convert_return_price",
    ],
    [],
  );

  const handleUpdateItem = useCallback(
    (
      index: number,
      key: keyof ReturnOrderItem,
      value: string | number,
    ) => {
      setItemDrafts((prev) =>
        prev.map((item, i) => {
          if (i !== index) return item;

          const shouldParseNumber = numericItemKeys.includes(key);
          const rawValue = shouldParseNumber ? parseNumber(value) : value;
          const nextItem = {
            ...item,
            [key]:
              key === "product_name" || key === "convert_operator"
                ? String(rawValue ?? "")
                : rawValue,
          } as ReturnOrderItem;

          const quantity = parseNumber(nextItem.quantity);
          const saleUnitPrice = parseNumber(nextItem.sale_unit_price);
          const returnUnitPrice = parseNumber(nextItem.return_unit_price);
          const convertQuantity = parseNumber(nextItem.convert_quantity);
          const convertUnitPrice = parseNumber(nextItem.convert_unit_price);

          const saleAmount =
            key === "sale_amount"
              ? parseNumber(rawValue)
              : quantity * saleUnitPrice || nextItem.sale_amount || 0;
          const returnAmount =
            key === "return_amount"
              ? parseNumber(rawValue)
              : quantity * returnUnitPrice || nextItem.return_amount || 0;
          const convertReturnPrice =
            key === "convert_return_price"
              ? parseNumber(rawValue)
              : convertQuantity * convertUnitPrice ||
              nextItem.convert_return_price ||
              0;

          return {
            ...nextItem,
            quantity,
            sale_unit_price: saleUnitPrice,
            sale_amount: saleAmount,
            return_unit_price: returnUnitPrice,
            return_amount: returnAmount,
            convert_quantity: convertQuantity,
            convert_unit_price: convertUnitPrice,
            convert_return_price: convertReturnPrice,
          };
        }),
      );
    },
    [numericItemKeys, parseNumber],
  );

  const handleRemoveItem = useCallback((index: number) => {
    setItemDrafts((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSaveItems = useCallback(() => {
    if (!selectedReturnOrder && selectedReturnOrderId !== "pinned") return;
    const normalizedItems = sanitizeItems(itemDrafts);

    if (!normalizedItems.length) {
      SnackbarUtils.error("Vui lòng thêm ít nhất 1 dòng chi tiết");
      return;
    }

    if (selectedReturnOrderId === "pinned") {
      const baseDraft =
        addRowRef.current ??
        ({
          return_date: todayString(),
          due_date: todayString(),
          customer_name: "",
          receiver_name: "",
          sale_order_code: "",
          payment_method: "",
          refund_now: true,
          no_refund: false,
          description: "",
          attachments: [],
        } as ReturnOrderRow);

      const mergedDraft = withItemFields({
        ...baseDraft,
        items: normalizedItems,
      });
      addRowRef.current = mergedDraft;
      setItemDrafts(normalizedItems);
      setAddRowVersion((v) => v + 1);
      SnackbarUtils.success("Đã lưu chi tiết cho chứng từ mới");
      return;
    }

    if (!selectedReturnOrder?.id) return;

    const mergedRow = withItemFields({
      ...selectedReturnOrder,
      items: normalizedItems,
    } as ReturnOrderRow);

    const payload = normalizePayload(mergedRow);

    updateReturnOrder.mutate(
      { id: selectedReturnOrder.id, ...payload },
      {
        onSuccess: (res) => {
          const next =
            res?.data && (res.data as ReturnOrderRow)
              ? withItemFields(res.data as ReturnOrderRow)
              : withItemFields(mergedRow);
          setSelectedReturnOrderId(next.id);
          upsertRow(next);
          SnackbarUtils.success("Đã lưu chi tiết chứng từ");
        },
        onError: () => {
          SnackbarUtils.error("Lưu chi tiết chứng từ không thành công");
        },
      },
    );
  }, [
    itemDrafts,
    normalizePayload,
    sanitizeItems,
    selectedReturnOrder,
    selectedReturnOrderId,
    updateReturnOrder,
    upsertRow,
    withItemFields,
  ]);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteReturnOrder.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa chứng từ không thành công");
      },
    });
  }, [
    confirmRow,
    deleteReturnOrder,
    editingId,
    removeRow,
    setEditingId,
  ]);

  const toggleInlineAddRow = useCallback(() => {
    setEditingId(null);
    gridApiRef.current?.stopEditing();
    setShowInlineAddRow((prev) => {
      const next = !prev ? "add" : "";
      if (next) {
        resetInlineAddRow();
        setSelectedReturnOrderId("pinned");
        setItemDrafts(sanitizeItems(addRowRef.current?.items));
      } else {
        addRowRef.current = null;
        setAddRowVersion((v) => v + 1);
        if (selectedReturnOrderId === "pinned") {
          setSelectedReturnOrderId(null);
          setItemDrafts([]);
        }
      }
      return next;
    });
  }, [resetInlineAddRow, sanitizeItems, selectedReturnOrderId, setEditingId]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: (params) =>
        params.data?.id === editingId || params.node.rowPinned === "top",
      flex: 1,
      minWidth: 140,
    }),
    [editingId],
  );

  const inlineAddRowData = useMemo(
    () => (showInlineAddRow ? (addRowRef.current ?? undefined) : undefined),
    [showInlineAddRow, addRowVersion],
  );

  const totalDraftReturnAmount = useMemo(
    () => {
      const normalized = sanitizeItems(itemDrafts);
      return normalized.reduce(
        (sum, item) =>
          sum +
          (parseNumber(
            item.return_amount ?? item.convert_return_price ?? 0,
          ) || 0),
        0,
      );
    },
    [itemDrafts, parseNumber, sanitizeItems],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          [Accounting] Return Orders
        </h1>
        <p className="text-sm text-gray-600">
          Quản lý chứng từ nhập lại hàng bán: tạo mới, chỉnh sửa nhanh trên bảng và xoá khi cần.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách chứng từ nhập lại
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và chỉnh sửa trực tiếp.
            </p>
          </div>
        </div>
        <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-gray-900">
                {selectedReturnOrder
                  ? `Chi tiết chứng từ${
                    selectedReturnOrder.sale_order_code
                      ? ` #${selectedReturnOrder.sale_order_code}`
                      : ""
                  }`
                  : "Chi tiết chứng từ"}
              </p>
              <p className="text-xs text-gray-500">
                {selectedReturnOrder
                  ? `${selectedReturnOrder.customer_name ?? ""} • ${selectedReturnOrder.return_date ?? ""
                  }`
                  : "Chưa chọn chứng từ"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleAddItem}
                disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60"
              >
                Thêm dòng
              </button>
              <button
                type="button"
                onClick={handleSaveItems}
                disabled={
                  (!selectedReturnOrder && selectedReturnOrderId !== "pinned") ||
                  updateReturnOrder.isPending
                }
                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-60"
              >
                {updateReturnOrder.isPending ? "Đang lưu..." : "Lưu chi tiết"}
              </button>
              <div className="text-right">
                <p className="text-xs text-gray-500">Tổng tiền hoàn</p>
                <p className="text-sm font-semibold text-gray-900">
                  {totalDraftReturnAmount.toLocaleString("vi-VN")}
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">#</th>
                  <th className="px-4 py-2 text-left font-semibold">WMS</th>
                  <th className="px-4 py-2 text-left font-semibold">Danh mục</th>
                  <th className="px-4 py-2 text-left font-semibold">Sản phẩm</th>
                  <th className="px-4 py-2 text-left font-semibold">ĐVT</th>
                  <th className="px-4 py-2 text-right font-semibold">SL bán</th>
                  <th className="px-4 py-2 text-right font-semibold">Đơn giá bán</th>
                  <th className="px-4 py-2 text-right font-semibold">Thành tiền bán</th>
                  <th className="px-4 py-2 text-right font-semibold">Đơn giá nhập lại</th>
                  <th className="px-4 py-2 text-right font-semibold">Thành tiền nhập lại</th>
                  <th className="px-4 py-2 text-left font-semibold">ĐVT quy đổi</th>
                  <th className="px-4 py-2 text-left font-semibold">Toán tử</th>
                  <th className="px-4 py-2 text-right font-semibold">Tỷ lệ</th>
                  <th className="px-4 py-2 text-right font-semibold">SL quy đổi</th>
                  <th className="px-4 py-2 text-right font-semibold">Đơn giá quy đổi</th>
                  <th className="px-4 py-2 text-right font-semibold">Thành tiền quy đổi</th>
                  <th className="px-4 py-2 text-center font-semibold">Hành động</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {itemDrafts.length ? (
                  itemDrafts.map((item, index) => (
                    <tr
                      key={`${selectedReturnOrder?.id ?? "draft"}-item-${index}`}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-4 py-2 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.wms_id ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "wms_id", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="WMS ID"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.category_id ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "category_id", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Danh mục"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.product_name ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "product_name", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Tên sản phẩm"
                        />
                        <div className="mt-1 text-xs text-gray-500">
                          ID:{" "}
                          <input
                            value={item.product_id ?? ""}
                            onChange={(e) =>
                              handleUpdateItem(index, "product_id", e.target.value)
                            }
                            disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                            className="w-28 rounded border border-gray-200 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none disabled:opacity-60"
                            placeholder="Mã"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.unit_id ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "unit_id", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Đơn vị"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.quantity ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "quantity", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.sale_unit_price ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "sale_unit_price", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.sale_amount ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "sale_amount", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.return_unit_price ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "return_unit_price", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.return_amount ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "return_amount", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.convert_unit_id ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "convert_unit_id", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="Đơn vị quy đổi"
                        />
                      </td>
                      <td className="px-4 py-2 text-gray-900">
                        <input
                          value={item.convert_operator ?? ""}
                          onChange={(e) =>
                            handleUpdateItem(index, "convert_operator", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="*, /, + ..."
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.convert_ratio ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "convert_ratio", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="1"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.convert_quantity ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "convert_quantity", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.convert_unit_price ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "convert_unit_price", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        <input
                          type="number"
                          value={item.convert_return_price ?? 0}
                          onChange={(e) =>
                            handleUpdateItem(index, "convert_return_price", e.target.value)
                          }
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="w-full rounded border border-gray-200 px-2 py-1 text-sm text-right focus:border-blue-500 focus:outline-none disabled:opacity-60"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          disabled={!selectedReturnOrder && selectedReturnOrderId !== "pinned"}
                          className="text-red-600 hover:text-red-700 text-xs font-semibold disabled:opacity-60"
                        >
                          Xóa
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="px-4 py-4 text-center text-gray-500"
                      colSpan={17}
                    >
                      {selectedReturnOrder
                        ? 'Chưa có dòng chi tiết. Bấm "Thêm dòng" để bắt đầu.'
                        : "Chọn một chứng từ để xem chi tiết."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center justify-end mb-3">
          <button
            type="button"
            onClick={toggleInlineAddRow}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4 text-green-700" />
            {showInlineAddRow ? "Ẩn dòng thêm mới" : "Thêm chứng từ"}
          </button>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          showAddRow={showInlineAddRow}
          addRowData={inlineAddRowData}
          defaultColDef={defaultColDef}
          totalPages={returnOrderData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          onSave={handleSaveRow}
          onAfterActions={() => {
            setShowInlineAddRow("");
            setEditingId(null);
          }}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onRowClicked={handleRowClick}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách chứng từ, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa chứng từ?"
        description={`Bạn chắc chắn muốn xóa chứng từ "${confirmRow?.sale_order_code ?? ""
          }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteReturnOrder.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default ReturnOrders;
